import { DEFAULT_PRODUCT, PRODUCTS, PRODUCT_HEADER, Product, fillProduct, productOfHost, type ProductSpec } from "@genbaryoku/contract";

/**
 * 製品（入口）の判定・画面（049、../eigyouryoku/docs/02 決めごと9）。画面のビルドは1つ。
 *   - 本番：location.hostname → 製品（genbaryoku.jp／eigyouryoku.jp。www. も）
 *   - 開発・テスト：?product=eigyo（開いたページのあいだだけ。localStorage には覚えない）か、ビルドの VITE_PRODUCT
 *   - ログイン後は Me.tenant.product が正（useProduct.ts）。ホストの製品と食い違えば、その製品のホストへ移す（開発は警告だけ）
 * ここは React に依らない（api.ts・labels.ts・main.tsx が読む）
 */
export type ProductSource = "host" | "query" | "env" | "default";
export type ProductInfo = ProductSpec & { product: Product };

function detect(): { product: Product; source: ProductSource } {
  const byHost = productOfHost(window.location.hostname);
  if (byHost) return { product: byHost, source: "host" };
  const q = Product.safeParse(new URLSearchParams(window.location.search).get("product"));
  if (q.success) return { product: q.data, source: "query" };
  const env = Product.safeParse(import.meta.env.VITE_PRODUCT);
  if (env.success) return { product: env.data, source: "env" };
  return { product: DEFAULT_PRODUCT, source: "default" };
}
const detected = detect();

/** 本番のホストで決まった製品。開発（localhost）は null */
export const HOST_PRODUCT: Product | null = detected.source === "host" ? detected.product : null;
/** ログイン前の製品（画面を開いたときに1回決める。SPA の中で URL が変わっても変えない） */
export const PRELOGIN_PRODUCT: Product = detected.product;

/** ログイン後の製品（Shell が Me.tenant.product を入れる）。決まり文の {product} と、ログイン前後で同じ答えを返すため */
let current: Product = PRELOGIN_PRODUCT;
export function setCurrentProduct(p: Product | undefined): void {
  if (p) current = p;
}
export function currentProduct(): Product {
  return current;
}

export function productInfo(p: Product): ProductInfo {
  return { ...PRODUCTS[p], product: p };
}

/** API に付けるヘッダー：開発・テストで製品を指しているときだけ x-product（本番のホストでは付けない＝API は Host で決める） */
export function productHeaders(): Record<string, string> {
  return detected.source === "query" || detected.source === "env" ? { [PRODUCT_HEADER]: detected.product } : {};
}

/** 決まり文の {product}（製品名）・{productHost}（ドメイン）をいまの製品に */
export function tp(text: string): string {
  return fillProduct(text, current);
}

/** <title>・favicon・manifest・theme-color を製品に（index.html は現場力で書いてあるので、営業力のときだけ差し替わる） */
export function applyBranding(p: Product): void {
  const a = PRODUCTS[p].assets;
  const name = PRODUCTS[p].name;
  if (document.title !== name && !document.title.startsWith(name)) document.title = name;
  document.documentElement.dataset.product = p;
  const set = (selector: string, href: string) => {
    const el = document.querySelector<HTMLLinkElement>(selector);
    if (el && el.getAttribute("href") !== href) el.setAttribute("href", href);
  };
  set('link[rel="icon"][type="image/svg+xml"]', a.logoMark);
  set('link[rel="icon"]:not([type])', a.favicon);
  set('link[rel="apple-touch-icon"]', a.appleTouchIcon);
  set('link[rel="manifest"]', a.manifest);
}

/**
 * 会社の製品とホストの製品が食い違う（例：営業力の会社なのに genbaryoku.jp を開いた）→ その製品のホストへ移す。
 * 開発（localhost）は移さず警告だけ。移したら true（画面はそれ以上描かない）
 */
export function redirectToProductHost(tenantProduct: Product): boolean {
  if (!HOST_PRODUCT || HOST_PRODUCT === tenantProduct) return false;
  const host = PRODUCTS[tenantProduct].host;
  window.location.replace(`https://${host}${window.location.pathname}${window.location.search}${window.location.hash}`);
  return true;
}
let warned = false;
export function warnProductMismatch(tenantProduct: Product): void {
  if (HOST_PRODUCT || warned || tenantProduct === PRELOGIN_PRODUCT) return;
  warned = true;
  console.warn(`[product] この会社は ${PRODUCTS[tenantProduct].name}（${tenantProduct}）ですが、画面は ${PRODUCTS[PRELOGIN_PRODUCT].name} で開いています。本番なら ${PRODUCTS[tenantProduct].host} へ移ります（開発は ?product=${tenantProduct} か VITE_PRODUCT）`);
}
